/**
 * JEDAN PRENOS — fajl ide na server jednom, a koraci čarobnjaka nose ručku.
 *
 * Do 09.10.2026. svaki korak (`/targets`, `/structure`, uvoz) i svaki „Browse"
 * slao je ceo fajl iznova. Sada se fajl otpremi jednom (`POST .../file`), a
 * koraci šalju JSON sa `file_handle`. Pravila brisanja su na serveru
 * (`apps/backend/middleware/importHandles.js`).
 *
 * Klijent drži JEDNU ručku, kao što i server drži jedan fajl po nalogu. Ključ
 * je izvor + ime + veličina + vreme izmene: isti fajl izabran ponovo sa diska
 * je nov `File` objekat, ali se ne šalje iznova. Fajl koji je pregledač
 * prepisao (`NOW()` u CSV/JSON) ima drugu veličinu ili vreme, pa ide ponovo —
 * to je drugi sadržaj.
 *
 * Kad server kaže 410 (ručka istekla, potrošena uvozom, ili posle odjave),
 * fajl se otpremi ponovo i korak ponovi — JEDNOM.
 */

/**
 * Odgovor uvoza kad kompozicija već ima redove a potvrde nema (HTTP 409).
 * Uvoz tada nije ni počeo, pa server fajl NE troši.
 */
export const POSTOJE_REDOVI = 1022;

/** @type {{kljuc: string, obecanje: Promise<object>} | null} */
let tekuca = null;

const kljucZa = (slug, file) => `${slug}|${file.name}|${file.size}|${file.lastModified}`;

const otpremi = async ({ URL, server, slug, file }) => {
    const telo = new FormData();
    telo.append('file', file);
    const res = await fetch(
        `${URL}/api/imports/${slug}/file?server_url=${encodeURIComponent(server || '')}`,
        { method: 'POST', credentials: 'include', body: telo },
    );
    return res.json();
};

/**
 * Ručka za fajl — postojeća ili tek otpremljena.
 *
 * Čuva se OBEĆANJE, ne ishod: izbor fajla odmah pokreće `/targets`, a brz klik
 * na „Browse" bi inače pokrenuo drugi prenos istog fajla dok prvi traje.
 */
export const ruckaZa = ({ URL, server, slug, file }) => {
    const kljuc = kljucZa(slug, file);
    if (tekuca?.kljuc !== kljuc) {
        const obecanje = otpremi({ URL, server, slug, file }).then((odgovor) => {
            // Neuspeo upload se ne pamti — sledeći pokušaj ide ispočetka.
            if (!odgovor?.status && tekuca?.obecanje === obecanje) tekuca = null;
            return odgovor;
        }, (greska) => {
            if (tekuca?.obecanje === obecanje) tekuca = null;
            throw greska;
        });
        tekuca = { kljuc, obecanje };
    }
    return tekuca.obecanje;
};

/**
 * Korak čarobnjaka nad fajlom: ručka umesto fajla, i JSON umesto multipart-a.
 *
 * @param {boolean} [trosi]  uvoz — server posle njega briše fajl
 * @returns {Promise<object>} JSON odgovor koraka, ili neuspeo odgovor uploada
 */
export const posaljiSaRuckom = async ({ URL, server, slug, file, ruta, polja, trosi = false }) => {
    const korak = async () => {
        const otpremljen = await ruckaZa({ URL, server, slug, file });
        if (!otpremljen?.status) return { odgovor: otpremljen, status: 0 };

        const res = await fetch(`${URL}/api/imports/${slug}${ruta}`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...polja, file_handle: otpremljen.handle }),
        });
        return { odgovor: await res.json(), status: res.status };
    };

    let { odgovor, status } = await korak();
    if (status === 410) {
        tekuca = null;
        ({ odgovor } = await korak());
    }
    // Uvoz troši fajl na serveru, uspeo ili ne; ručka se više ne nudi.
    if (trosi && odgovor?.code !== POSTOJE_REDOVI) tekuca = null;
    return odgovor;
};

/**
 * Odustajanje: čarobnjak zatvoren. Server bi fajl ionako obrisao posle 30
 * minuta, ali nema razloga da do tada zauzima disk.
 */
export const odustani = async (URL) => {
    const bila = tekuca;
    tekuca = null;
    if (!bila) return;
    try {
        const { handle } = await bila.obecanje;
        if (handle) {
            await fetch(`${URL}/api/imports/file/${handle}`, { method: 'DELETE', credentials: 'include' });
        }
    } catch {
        // Neuspelo odustajanje nije greška korisnika; server briše po isteku.
    }
};

/** Samo za testove. */
export const _zaboravi = () => { tekuca = null; };
